import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock } from 'lucide-react';
import { SignIn, SignUp, useAuth as useClerkAuth } from '@clerk/react';

// Mirror the guard used in AuthContext: without a Clerk publishable key there is
// no <ClerkProvider>, so Clerk hooks/components must not be rendered at all.
const HAS_CLERK = Boolean(process.env.REACT_APP_CLERK_PUBLISHABLE_KEY);

/* Shared modal chrome so both the Clerk body and the fallback look identical. */
const ModalShell = ({ onClose, children }) => (
  <motion.div
    className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/45 p-4 backdrop-blur-sm"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
  >
    <motion.div
      className="relative"
      initial={{ scale: 0.96, opacity: 0, y: 10 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      exit={{ scale: 0.96, opacity: 0, y: 10 }}
      transition={{ duration: 0.2 }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute -top-3 -right-3 z-10 grid place-items-center w-9 h-9 rounded-full bg-white border border-[rgba(32,30,29,0.12)] text-sand-600 hover:text-ink shadow-brand-md transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      {children}
    </motion.div>
  </motion.div>
);

/* Clerk-backed body — only mounted when Clerk is configured, so useClerkAuth()
   and <SignIn>/<SignUp> always run inside a real <ClerkProvider>. */
const ClerkAuthBody = ({ onClose, onAuthSuccess, initialMode }) => {
  const { isSignedIn } = useClerkAuth();

  useEffect(() => {
    if (isSignedIn) {
      if (onAuthSuccess) onAuthSuccess();
      if (onClose) onClose();
    }
  }, [isSignedIn, onAuthSuccess, onClose]);

  return initialMode === 'signup' ? <SignUp routing="virtual" /> : <SignIn routing="virtual" />;
};

/* Graceful fallback when no Clerk key is present (e.g. a deploy missing
   REACT_APP_CLERK_PUBLISHABLE_KEY). The site stays up instead of white-screening. */
const AuthUnavailable = ({ onClose }) => (
  <div className="w-[340px] max-w-full rounded-brand-lg border border-[rgba(32,30,29,0.12)] bg-white p-7 text-center shadow-brand-lg">
    <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-sand-100 text-sand-600">
      <Lock className="h-5 w-5" strokeWidth={2.25} />
    </div>
    <h3 className="font-display text-2xl text-ink">Sign-in isn’t available</h3>
    <p className="mt-2 text-[14px] leading-relaxed text-sand-700">
      Accounts aren’t configured for this deployment yet. You can still explore everything
      locally in demo mode — no sign-in required.
    </p>
    <button
      type="button"
      onClick={onClose}
      className="mt-6 w-full rounded-full bg-bill-500 py-3 text-sm font-semibold text-white shadow-brand-sm transition-colors hover:bg-bill-600"
    >
      Continue in demo mode
    </button>
  </div>
);

/**
 * Themed auth modal. Keeps the original props (isOpen / onClose /
 * onAuthSuccess / initialMode) so LandingPage and DashboardPage don't change.
 * When Clerk is configured it renders Clerk's <SignIn>/<SignUp> and auto-closes
 * on sign-in; otherwise it shows a graceful "not configured" notice.
 */
const AuthModal = ({ isOpen, onClose, onAuthSuccess, initialMode = 'signin' }) => (
  <AnimatePresence>
    {isOpen && (
      <ModalShell onClose={onClose}>
        {HAS_CLERK ? (
          <ClerkAuthBody onClose={onClose} onAuthSuccess={onAuthSuccess} initialMode={initialMode} />
        ) : (
          <AuthUnavailable onClose={onClose} />
        )}
      </ModalShell>
    )}
  </AnimatePresence>
);

export default AuthModal;
